"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FileListItem = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const Link_1 = require("../../1-inline/Link");
const SpinnerCircle_1 = require("../../2-inline-block/SpinnerCircle");
const context_1 = require("../../styles/context");
const Ripple_1 = require("../../misc/Ripple");
const environment_1 = require("../../utils/environment");
const rowClass = (0, nano_theme_1.drule)({
    d: 'flex',
    ai: 'center',
    gap: '4px',
    w: '100%',
    minWidth: 0,
    bxz: 'border-box',
    pd: '4px',
    bdrad: '14px',
    us: 'none',
    cur: 'default',
    trs: 'background .16s ease, box-shadow .16s ease, opacity .16s ease',
});
const surfaceClass = (0, nano_theme_1.rule)({
    d: 'flex',
    ai: 'center',
    gap: '12px',
    flex: '1 1 auto',
    minWidth: 0,
    bxz: 'border-box',
    pd: '10px 6px 10px 12px',
    mar: 0,
    bd: 0,
    bg: 'none',
    td: 'none',
    ta: 'left',
    bdrad: '10px',
    appearance: 'none',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    col: 'inherit',
    cur: 'inherit',
    trs: 'padding .2s ease',
});
const surfaceSmallClass = (0, nano_theme_1.rule)({
    pd: '0px 6px 0px 12px',
});
const metadataWrapperClass = (0, nano_theme_1.rule)({
    d: 'grid',
    gridTemplateRows: '1fr',
    op: 1,
    trs: 'grid-template-rows .2s ease, opacity .2s ease',
});
const metadataWrapperSmallClass = (0, nano_theme_1.rule)({
    gridTemplateRows: '0fr',
    op: 0,
});
const metadataInnerClass = (0, nano_theme_1.rule)({
    minHeight: 0,
    ov: 'hidden',
});
const iconClass = (0, nano_theme_1.drule)({
    d: 'flex',
    ai: 'center',
    jc: 'center',
    flex: '0 0 auto',
    bdrad: '10px',
});
const contentClass = (0, nano_theme_1.rule)({
    d: 'flex',
    fld: 'column',
    justifyContent: 'center',
    flex: '1 1 auto',
    minWidth: 0,
});
const titleClass = (0, nano_theme_1.drule)({
    ...nano_theme_1.lightTheme.font.ui1.mid,
    d: 'block',
    minWidth: 0,
    ov: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fz: '15px',
    lh: '20px',
});
const metadataClass = (0, nano_theme_1.drule)({
    ...nano_theme_1.lightTheme.font.ui1.mid,
    d: 'block',
    minWidth: 0,
    ov: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fz: '12px',
    lh: '16px',
    mrt: '2px',
});
const titleSelector = 'jjFileListItemTitle';
const rowSelector = 'jjFileListItemRow';
const actionsClass = (0, nano_theme_1.rule)({
    d: 'flex',
    ai: 'center',
    gap: '4px',
    flex: '0 0 auto',
    pd: '0 4px 0 0',
    op: 0,
    trs: 'opacity .16s ease',
    [`.${rowSelector}:hover &`]: {
        op: 1,
    },
});
const rowMutedClass = (0, nano_theme_1.rule)({
    [`.${titleSelector}`]: {
        op: 0.7,
    },
    [`&:hover .${titleSelector}`]: {
        op: 1,
    },
});
const FileListItem = ({ title, metadata, icon, iconHover, actions, to, selected, disabled, loading, className = '', style, type, external, spacious, small, muted, fill, ...rest }) => {
    const styles = (0, context_1.useStyles)();
    const isDisabled = !!disabled || !!loading;
    const [hovered, setHovered] = React.useState(false);
    const iconNode = loading ? React.createElement(SpinnerCircle_1.SpinnerCircle, { color: styles.g(0.45) }) : hovered ? iconHover : icon;
    const selectedBg = selected
        ? styles.col.get('neutral', 'bg-2')
        : fill
            ? styles.col.get('neutral', 'bg-1')
            : 'transparent';
    const hoverBg = selected ? styles.col.accent(0, 'el-1') : styles.g(0, 0.04);
    const rowDynamic = rowClass({
        bg: selectedBg,
        '&:hover': isDisabled
            ? undefined
            : {
                bg: hoverBg,
            },
    });
    const iconDynamic = iconClass({
        col: selected ? styles.col.accent(0, 'solid-1') : isDisabled ? styles.g(0.55) : styles.g(0.45),
        bg: styles.g(0, 0.03),
        w: spacious ? '48px' : '36px',
        h: spacious ? '48px' : '36px',
    });
    const titleDynamic = titleClass({ col: isDisabled ? styles.g(0.45) : styles.g(0.1) });
    const metadataDynamic = metadataClass({ col: isDisabled ? styles.g(0.56) : styles.g(0.42) });
    const content = (React.createElement(React.Fragment, null,
        !!iconNode && React.createElement("span", { className: iconDynamic }, iconNode),
        React.createElement("span", { className: contentClass },
            React.createElement("span", { className: titleSelector + ' ' + titleDynamic }, title),
            metadata !== undefined && metadata !== null && metadata !== false && (React.createElement("span", { className: metadataWrapperClass + (small ? metadataWrapperSmallClass : '') },
                React.createElement("span", { className: metadataInnerClass },
                    React.createElement("span", { className: metadataDynamic }, metadata)))))));
    const interactiveProps = {
        ...rest,
        className: surfaceClass + (small ? surfaceSmallClass : ''),
        'aria-busy': loading || undefined,
        'aria-disabled': isDisabled || undefined,
    };
    let surface;
    let clickable = false;
    if (to && !isDisabled) {
        clickable = true;
        surface = (React.createElement(Link_1.Link, { ...interactiveProps, a: true, to: to, external: external }, content));
    }
    else if (rest.onClick && !isDisabled) {
        clickable = true;
        surface = (React.createElement("button", { ...interactiveProps, type: type || 'button', disabled: isDisabled }, content));
    }
    else {
        surface = React.createElement("div", { ...interactiveProps }, content);
    }
    const rowStyle = { ...style, opacity: isDisabled ? 0.68 : undefined };
    if (spacious) {
        rowStyle.paddingTop = 12;
        rowStyle.paddingBottom = 12;
    }
    const optionsStyle = {};
    if (environment_1.isTouch) {
        optionsStyle.opacity = 1;
    }
    if (isDisabled) {
        optionsStyle.pointerEvents = 'none';
        optionsStyle.opacity = 0.6;
    }
    surface = (React.createElement("div", { className: className + ' ' + rowSelector + ' ' + rowDynamic + (muted ? ' ' + rowMutedClass : ''), style: rowStyle, onMouseEnter: iconHover ? () => setHovered(true) : void 0, onMouseLeave: iconHover ? () => setHovered(false) : void 0 },
        surface,
        !!actions && (React.createElement("span", { className: actionsClass, style: optionsStyle }, actions))));
    if (clickable) {
        surface = React.createElement(Ripple_1.Ripple, null, surface);
    }
    return surface;
};
exports.FileListItem = FileListItem;
exports.default = exports.FileListItem;
